import React from 'react'
import { Box, Card, CardContent, Chip, Typography } from '@mui/material'
import { green, orange, red } from '@mui/material/colors'

import { IContractDates, formatDay } from '../../../Services/contractDates'
import { ContractDatesForm } from './ContractDatesForm'
import { DwMarginField } from './DwMarginField'
import { useContractDates } from './useContractDates'

interface Props {
	billingAccountId: string
	/** Compte sous contrat : au moins une ligne produit posée en admin. */
	underContract: boolean
	/** Au moins un abonnement Stripe vivant (active / trialing). */
	hasLiveSubscription: boolean
	/** Rechargement de la fiche après enregistrement du taux. */
	onChanged?: () => void
}

// Le statut d'un compte, à deux lectures.
//
// ⚠️ Un compte sous contrat n'a PAS d'abonnement Stripe : le chip
// « No active subscription » d'avant était donc affiché en permanence, en rouge,
// sur des comptes parfaitement en règle. L'opérateur n'avait aucun moyen de
// savoir si le contrat courait encore.
//
// Sous contrat, c'est la date de fin qui fait foi ; sinon, c'est Stripe.

// #region contractState
const contractState = (c: IContractDates | null) => {
	if (!c?.startAt) {
		return { label: 'Contrat sans dates', color: orange[700] as string, hint: 'Sans date de début, aucune année de référence ne se calcule : la consommation Store et Track de ce compte n’est rattachée à aucune période.' }
	}
	if (c.ended) {
		return { label: `Contrat terminé le ${formatDay(c.endAt)}`, color: red[500] as string, hint: 'Les droits produit du compte sont fermés. Repousse la date de fin pour les rouvrir — les quantités convenues sont intactes.' }
	}
	if (c.notStarted) {
		return { label: `Contrat à partir du ${formatDay(c.startAt)}`, color: orange[700] as string, hint: 'Les droits sont déjà ouverts : on ne verrouille pas un client avant son premier jour.' }
	}
	return {
		label: c.endAt ? `Contrat jusqu’au ${formatDay(c.endAt)}` : 'Contrat en cours',
		color: green[500] as string,
		hint: c.endAt ? '' : 'Sans date de fin, le contrat court : il ne se termine pas tout seul.',
	}
}

// #endregion

// #region BillingAccountContract
export const BillingAccountContract: React.FC<Props> = ({
	billingAccountId,
	underContract,
	hasLiveSubscription,
	onChanged,
}) => {
	const { contract, save, saving } = useContractDates(billingAccountId, underContract)

	if (!underContract) {
		return (
			<Card style={{ marginBottom: 20, width: '100%' }}>
				<CardContent>
					<Typography variant="h6" gutterBottom>
						Statut
					</Typography>
					<Chip
						label={hasLiveSubscription ? 'Abonnement actif' : 'Aucun abonnement actif'}
						sx={{ bgcolor: hasLiveSubscription ? green[500] : red[500], color: 'white', fontWeight: 'bold' }}
						size="small"
					/>
					<Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
						Pay as you go : c’est Stripe qui fixe la période, et la renouvelle chaque mois.
					</Typography>
					{/* ⚠️ Le taux vaut pour TOUS les comptes : la refacturation du
					    datawarehouse ne dépend pas du mode de facturation. */}
					<DwMarginField billingAccountId={billingAccountId} onSaved={() => onChanged?.()} />
				</CardContent>
			</Card>
		)
	}

	const state = contractState(contract)
	return (
		<Card style={{ marginBottom: 20, width: '100%' }}>
			<CardContent>
				<Typography variant="h6" gutterBottom>
					Statut du contrat
				</Typography>
				<Chip label={state.label} sx={{ bgcolor: state.color, color: 'white', fontWeight: 'bold' }} size="small" />
				{state.hint && (
					<Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
						{state.hint}
					</Typography>
				)}

				{/* ⚠️ Cet abonnement prélève le client EN PLUS de sa facture Pennylane :
				    on le dit ici, c'est le seul endroit où quelqu'un le cherchera. */}
				{hasLiveSubscription && (
					<Typography variant="caption" sx={{ display: 'block', mt: 1, color: orange[800] }}>
						⚠️ Un abonnement Stripe tourne encore sur ce compte — il prélève en plus du contrat.
					</Typography>
				)}

				{contract?.yearStart && (
					<Box sx={{ mt: 1.5 }}>
						<Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
							Année de référence en cours : <strong>{formatDay(contract.yearStart)}</strong> →{' '}
							<strong>{formatDay(contract.yearLastDay)}</strong>
						</Typography>
						{/* ⚠️ Une année coupée par la date de fin fait moins de 365 jours.
						    Sans le dire, sa consommation mécaniquement plus basse se compare
						    à une année pleine et se lit comme une chute d'usage. */}
						{!!contract.yearDays && contract.yearDays < 365 && (
							<Typography variant="caption" sx={{ display: 'block', color: orange[800] }}>
								Période raccourcie par la date de fin : {contract.yearDays} jours.
							</Typography>
						)}
					</Box>
				)}

				<ContractDatesForm contract={contract} saving={saving} onSave={save} />

				<DwMarginField billingAccountId={billingAccountId} onSaved={() => onChanged?.()} />

				{/* ⚠️ Rien n'est facturé sur cette période : l'ajustement est commercial,
				    fait une fois par an en regardant les volumes. C'est la seule phrase
				    qui empêche quelqu'un d'attendre une facture qui ne viendra pas. */}
				<Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
					Ces dates découpent l’année de référence, sur laquelle se relève la consommation
					Store et Track du compte. Rien n’y est facturé automatiquement : l’ajustement se
					fait à la main, au vu des volumes.
				</Typography>
			</CardContent>
		</Card>
	)
}

// #endregion
